import React, { useState, useEffect } from "react";
import { sessionService } from "../services/session.service";
import { ThemePicker } from "./ThemePicker";

interface UserProfileProps {
  onClose?: () => void;
}

export function UserProfile({ onClose }: UserProfileProps) {
  const [displayName, setDisplayName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const name = sessionService.getPlayerName();
    if (name) {
      setDisplayName(name);
    } else {
      // If no name, get or create user to ensure userId exists
      const user = sessionService.getOrCreateUser();
      setDisplayName(user.displayName);
    }
  }, []);

  const handleSave = () => {
    if (!displayName.trim()) {
      setError("Tên không được để trống");
      return;
    }

    if (displayName.trim().length > 20) {
      setError("Tên không được quá 20 ký tự");
      return;
    }

    setError("");
    sessionService.updateDisplayName(displayName.trim());
    setIsEditing(false);
  };

  const handleCancel = () => {
    const name = sessionService.getPlayerName();
    setDisplayName(name || "");
    setIsEditing(false);
    setError("");
  };

  const session = sessionService.getSession();
  const userId = session?.userId;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>👤 Hồ sơ người chơi</h2>
          {onClose && (
            <button onClick={onClose} style={styles.closeButton}>
              ✕
            </button>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.section}>
          <label style={styles.label}>Tên hiển thị</label>
          {isEditing ? (
            <div style={styles.editGroup}>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={styles.input}
                maxLength={20}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
              />
              <div style={styles.buttonGroup}>
                <button onClick={handleSave} style={styles.saveButton}>
                  Lưu
                </button>
                <button onClick={handleCancel} style={styles.cancelButton}>
                  Hủy
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.displayGroup}>
              <span style={styles.displayName}>{displayName || "Chưa có tên"}</span>
              <button onClick={() => setIsEditing(true)} style={styles.editButton}>
                ✏️ Chỉnh sửa
              </button>
            </div>
          )}
        </div>

        {userId && (
          <div style={styles.section}>
            <label style={styles.label}>User ID</label>
            <div style={styles.userId}>{userId}</div>
            <p style={styles.hint}>
              ID này được lưu tự động để bạn có thể tiếp tục chơi sau khi tải lại trang
            </p>
          </div>
        )}

        <div style={styles.section}>
          <label style={styles.label}>Thống kê</label>
          <p style={styles.statsHint}>
            Thống kê sẽ được cập nhật sau mỗi ván chơi
          </p>
          {/* Stats will be displayed here when we add API to fetch user stats */}
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Giao diện</label>
          <ThemePicker />
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "20px",
    background: "var(--bg)",
  },
  card: {
    background: "var(--bg-card)", // Use theme variable for consistency
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    borderRadius: "16px",
    padding: "30px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: "500px",
    width: "100%",
    position: "relative",
    border: "1px solid var(--border-subtle)", // Use theme variable for consistency
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    color: "var(--text)",
  },
  closeButton: {
    background: "rgba(255,255,255,0.1)",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "var(--text)",
    padding: "0",
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  section: {
    marginBottom: "25px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text-muted)",
    marginBottom: "8px",
  },
  displayGroup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px",
    background: "var(--bg-card-hover)", // Use theme variable for consistency
    borderRadius: "8px",
  },
  displayName: {
    fontSize: "16px",
    color: "var(--text)",
    fontWeight: "500",
  },
  editButton: {
    padding: "6px 12px",
    fontSize: "14px",
    background: "var(--accent)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  editGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  input: {
    padding: "12px",
    fontSize: "16px",
    border: "2px solid var(--border-medium)",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    background: "rgba(20, 20, 30, 0.8)",
    color: "var(--text)",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  saveButton: {
    flex: 1,
    padding: "10px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    background: "var(--accent)",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  cancelButton: {
    flex: 1,
    padding: "10px",
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text)",
    background: "rgba(255,255,255,0.1)",
    border: "2px solid var(--border-medium)",
    borderRadius: "6px",
    cursor: "pointer",
  },
  userId: {
    padding: "10px",
    background: "rgba(40, 40, 60, 0.6)",
    borderRadius: "8px",
    fontSize: "12px",
    fontFamily: "monospace",
    color: "var(--text-muted)",
    wordBreak: "break-all",
  },
  hint: {
    marginTop: "8px",
    fontSize: "12px",
    color: "rgba(255,255,255,0.6)",
  },
  statsHint: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.6)",
    fontStyle: "italic",
  },
  error: {
    padding: "12px",
    background: "rgba(231, 76, 60, 0.2)",
    color: "#ffcccc",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px",
    border: "1px solid rgba(231, 76, 60, 0.5)",
  },
};
